import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { paymentAPI } from "../../services/api.service";
import { useAuth } from "../../context/AuthContext";
import StateAutoSuggest from "../../components/StateAutoSuggest/StateAutoSuggest";
import "../styles/CheckoutPage.css";

const COMPANY_STATE = "Maharashtra";
const CGST_RATE = 0.09;
const SGST_RATE = 0.09;
const IGST_RATE = 0.18;

const calculateTaxBreakdown = (basePrice, placeOfSupply) => {
  const normalizedState = (placeOfSupply || "").trim().toLowerCase();
  const isMaharashtra = normalizedState === COMPANY_STATE.toLowerCase();
  const cgstAmount = isMaharashtra ? Math.round(basePrice * CGST_RATE * 100) / 100 : 0;
  const sgstAmount = isMaharashtra ? Math.round(basePrice * SGST_RATE * 100) / 100 : 0;
  const igstAmount = !isMaharashtra ? Math.round(basePrice * IGST_RATE * 100) / 100 : 0;
  const totalTax = cgstAmount + sgstAmount + igstAmount;

  return {
    basePrice,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTax,
    totalPrice: basePrice + totalTax,
    taxType: isMaharashtra ? "cgst_sgst" : "igst",
  };
};

const PLANS = {
  basic_listing: {
    name: "Basic Plan",
    basePrice: 100,
    features: ["1 property listing", "1 month validity"],
  },
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const planId = location.state?.planId || "basic_listing";
  const renewPropertyId = location.state?.renewPropertyId || null;
  const returnTo = location.state?.returnTo || "/seller-dashboard/properties";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gstNumber, setGstNumber] = useState(location.state?.gstNumber || "");
  const [gstError, setGstError] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState(location.state?.placeOfSupply || "");
  const [placeOfSupplyError, setPlaceOfSupplyError] = useState("");

  const plan = PLANS[planId] || PLANS.basic_listing;
  const pricing = calculateTaxBreakdown(plan.basePrice, placeOfSupply || COMPANY_STATE);

  const validateGSTNumber = (gst) => {
    if (!gst.trim()) {
      return true;
    }

    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst.toUpperCase());
  };

  const handleGstNumberChange = (e) => {
    const value = e.target.value.toUpperCase();
    setGstNumber(value);

    if (value.trim() && !validateGSTNumber(value)) {
      setGstError("Invalid GST Number format. Example: 27AAPCT0055K1Z0");
    } else {
      setGstError("");
    }
  };

  const handlePlaceOfSupplyChange = (value) => {
    setPlaceOfSupply(value || "");
    if (value?.trim()) {
      setPlaceOfSupplyError("");
    }
  };

  useEffect(() => {
    if (!planId || !PLANS[planId]) {
      navigate("/seller-dashboard/plans");
    }
  }, [planId, navigate]);

  useEffect(() => {
    if (!placeOfSupply && user?.state) {
      setPlaceOfSupply(user.state);
    }
  }, [placeOfSupply, user]);

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });
  };

  const handlePayNow = async () => {
    if (!placeOfSupply.trim()) {
      setPlaceOfSupplyError("Place of Supply is required before payment");
      return;
    }

    if (gstNumber.trim() && !validateGSTNumber(gstNumber)) {
      setGstError("Invalid GST Number format. Example: 27AAPCT0055K1Z0");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await paymentAPI.createOrder(planId, {
        gst_number: gstNumber.toUpperCase().trim() || null,
        place_of_supply: placeOfSupply.trim(),
      });

      const {
        order_id,
        key_id,
        amount,
        baseAmount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        place_of_supply,
        tax_type,
      } = data;

      await loadRazorpayScript();

      const baseAmountInRupees = baseAmount / 100;
      const cgstAmountInRupees = cgstAmount / 100;
      const sgstAmountInRupees = sgstAmount / 100;
      const igstAmountInRupees = igstAmount / 100;

      const options = {
        key: key_id,
        amount,
        currency: "INR",
        name: "360Coordinates",
        description: plan.name,
        order_id,
        notes: {
          baseAmount: baseAmountInRupees.toFixed(2),
          cgstAmount: cgstAmountInRupees.toFixed(2),
          sgstAmount: sgstAmountInRupees.toFixed(2),
          igstAmount: igstAmountInRupees.toFixed(2),
          placeOfSupply: place_of_supply,
          taxType: tax_type,
          convenienceCharges: "0.00",
          gstOnCharges: "0.00",
        },
        handler: (response) => {
          navigate("/seller-dashboard/payment-success", {
            state: {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planId,
              plan,
              baseAmount: baseAmountInRupees,
              cgstAmount: cgstAmountInRupees,
              sgstAmount: sgstAmountInRupees,
              igstAmount: igstAmountInRupees,
              gst_number: gstNumber.toUpperCase().trim() || null,
              place_of_supply,
              tax_type,
              renewPropertyId,
              returnTo,
            },
          });
        },
        prefill: {
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#8b5cf6" },
        modal: { ondismiss: () => setLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setError(response.error?.description || "Payment failed");
        setLoading(false);
        navigate("/seller-dashboard/payment-failed", {
          state: { error: response.error?.description, returnTo },
        });
      });
      rzp.open();
      setLoading(false);
    } catch (err) {
      setError(err.message || "Failed to initiate payment");
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <header className="checkout-header">
          <h1>Checkout</h1>
          <p>Review your order and proceed to payment</p>
        </header>

        <div className="checkout-card">
          <h2>Order Summary</h2>
          <div className="checkout-summary">
            <div className="checkout-row">
              <span>Plan</span>
              <span className="checkout-value">{plan.name}</span>
            </div>
            <div className="checkout-row">
              <span>Features</span>
              <span className="checkout-features">{plan.features.join(" | ")}</span>
            </div>
            <div className="checkout-divider" />
          </div>

          <h3 className="checkout-gst-title">Place of Supply</h3>
          <div className="checkout-gst-section">
            <div className="gst-input-group">
              <label className="gst-label">
                Select State
                <span className="gst-help-text">
                  Select the purchaser state. Maharashtra applies 9% CGST + 9% SGST. Other states apply 18% IGST.
                </span>
              </label>
              <StateAutoSuggest
                value={placeOfSupply}
                onChange={handlePlaceOfSupplyChange}
                placeholder="Select state"
                disabled={loading}
                error={placeOfSupplyError}
                className="checkout-state-autosuggest"
              />
              {placeOfSupplyError && <span className="gst-error-message">{placeOfSupplyError}</span>}
            </div>
          </div>

          <h3 className="checkout-gst-title">Tax Information (Optional)</h3>
          <div className="checkout-gst-section">
            <div className="gst-input-group">
              <label htmlFor="gst-number" className="gst-label">
                GST Number (Optional)
                <span className="gst-help-text">Enter your 15-digit GST number for business invoicing</span>
              </label>
              <input
                id="gst-number"
                type="text"
                placeholder="Example: 27AAPCT0055K1Z0"
                value={gstNumber}
                onChange={handleGstNumberChange}
                disabled={loading}
                className={`gst-input ${gstError ? "gst-input-error" : ""}`}
                maxLength="15"
              />
              {gstError && <span className="gst-error-message">{gstError}</span>}
            </div>
          </div>

          <h3 className="checkout-fee-breakup-title">Fee Breakup</h3>
          <div className="checkout-fee-breakup">
            <div className="fee-row">
              <span className="fee-label">Amount</span>
              <span className="fee-value">Rs. {plan.basePrice.toFixed(2)}</span>
            </div>
            <div className="fee-row">
              <span className="fee-label">Convenience Charges</span>
              <span className="fee-value">Rs. 0.00</span>
            </div>
            {pricing.taxType === "cgst_sgst" ? (
              <>
                <div className="fee-row">
                  <span className="fee-label">CGST 9%</span>
                  <span className="fee-value">Rs. {pricing.cgstAmount.toFixed(2)}</span>
                </div>
                <div className="fee-row">
                  <span className="fee-label">SGST 9%</span>
                  <span className="fee-value">Rs. {pricing.sgstAmount.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="fee-row">
                <span className="fee-label">IGST 18%</span>
                <span className="fee-value">Rs. {pricing.igstAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="fee-divider" />
            <div className="fee-row fee-total">
              <span className="fee-label">Total Charges</span>
              <span className="fee-value">Rs. {pricing.totalPrice.toFixed(2)}</span>
            </div>
          </div>
          {error && <p className="checkout-error">{error}</p>}
          <button className="checkout-pay-btn" onClick={handlePayNow} disabled={loading || !placeOfSupply}>
            {loading ? "Processing..." : "Pay Now"}
          </button>
          <button className="checkout-back-btn" onClick={() => navigate("/seller-dashboard/plans")}>
            Back to Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
